<script setup lang="ts">
import { computed, useAttrs } from 'vue'

import { useUiKitLink } from '@ui/composables/useUiKitLink'

import AppIcon from '@ui/app-icon/AppIcon.vue'

import type { AppIconButtonEmits, AppIconButtonProps } from './types'

const props = withDefaults(defineProps<AppIconButtonProps>(), {
  type: 'button',
  variant: 'close',
  size: 'md',
  disabled: false,
  loading: false,
  icon: '',
  to: '',
})

const emit = defineEmits<AppIconButtonEmits>()
const externalAttrs = useAttrs()

const classes = computed(() => ([
  `variant-${props.variant}`,
  `size-${props.size}`,
  { disabled: props.disabled || props.loading },
  { loading: props.loading },
]))

const { linkComponent } = useUiKitLink()

const isDisabled = computed(() => props.disabled || props.loading)
const isLinkDisabled = computed(() => Boolean(props.to) && isDisabled.value)

const component = computed(() => {
  if (props.to?.startsWith('http') || isLinkDisabled.value) {
    return 'a'
  }

  return props.to ? linkComponent || 'a' : 'button'
})

const attrs = computed(() => {
  const extra: Record<string, string> = {}

  if (isLinkDisabled.value) {
    extra.role = 'link'
  } else if (props.to?.startsWith('http')) {
    extra.href = props.to
    extra.target = '_blank'
    extra.rel = 'noopener noreferrer'
  } else if (props.to && linkComponent) {
    extra.to = props.to
  } else if (props.to) {
    extra.href = props.to
  }

  return {
    ...externalAttrs,
    ...extra,
  }
})

const buttonType = computed(() => props.to ? undefined : props.type)
const nativeDisabled = computed(() => (props.to ? undefined : isDisabled.value))

function handleClick(event: MouseEvent) {
  if (isDisabled.value) {
    event.preventDefault()
    return
  }

  emit('click', event)
}
</script>

<template>
  <component
    :is="component"
    class="app-button"
    :class="classes"
    :disabled="nativeDisabled"
    :type="buttonType"
    :aria-busy="loading"
    :aria-disabled="isDisabled || undefined"
    data-test-id="app-icon-button"
    v-bind="attrs"
    @click="handleClick"
  >
    <span class="app-button-body">
      <app-icon
        v-if="icon"
        class="app-button-icon"
        :name="icon"
      />
    </span>
  </component>
</template>

<style lang="scss" scoped>
.app-button {
  --icon-size: 16px;
  --spinner-color: var(--g-neutral-2);

  position: relative;
  margin: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border: 1px solid transparent;
  background-color: transparent;
  outline: 2px solid transparent;
  outline-offset: 2px;
  font-size: var(--icon-size);
  cursor: pointer;
  transition:
    background-color 0.2s ease-in-out,
    color 0.2s ease-in-out,
    border-color 0.2s ease-in-out,
    opacity .2s ease-in-out;

  &-body {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:where(.variant-close) {
    border-radius: 50%;
    background-color: var(--g-neutral-7);
    color: var(--g-neutral-2);

    &:hover:not(.disabled) {
      background-color: var(--g-neutral-6);
    }

    &.disabled {
      opacity: .6;
    }
  }

  &:where(.variant-no-fill) {
    background-color: transparent;
    color: var(--g-neutral-2);

    &:hover:not(.disabled) {
      border-color: var(--g-neutral-5);
    }

    &.disabled {
      background-color: var(--g-neutral-7);
      opacity: .6;
    }
  }

  &:where(.variant-filled) {
    background-color: var(--g-neutral-7);
    color: var(--g-neutral-1);

    &:hover:not(.disabled) {
      background-color: var(--g-neutral-6);
    }

    &.disabled {
      opacity: .6;
    }
  }

  &:where(.size-md) {
    &:not(.variant-close) {
      --icon-size: 20px;

      width: 36px;
      height: 36px;
      border-radius: 12px;
    }

    &.variant-close {
      --icon-size: 16px;

      width: 32px;
      height: 32px;
    }
  }

  &:where(.size-sm) {
    &:not(.variant-close) {
      --icon-size: 16px;

      width: 28px;
      height: 28px;
      border-radius: 8px;
    }

    &.variant-close {
      --icon-size: 16px;

      width: 24px;
      height: 24px;
    }
  }

  &:where(.size-xs) {
    &:not(.variant-close) {
      --icon-size: 14px;

      transform: translateY(1px);
      width: 20px;
      height: 20px;
      border-radius: 6px;
    }

    &.variant-close {
      --icon-size: 8px;

      transform: translateY(1px);
      width: 16px;
      height: 16px;
    }
  }

  &:focus-visible {
    outline-color: var(--a-lilac);
  }

  &:where(.disabled) {
    cursor: not-allowed;
  }

  &.loading {
    color: transparent;
    cursor: progress;

    &::before {
      position: absolute;
      inset: 0;
      margin: auto;
      display: inline-block;
      width: var(--icon-size);
      height: var(--icon-size);
      border: 2px solid var(--spinner-color);
      border-radius: 50%;
      animation: app-spin var(--app-spin-duration) linear infinite;
      border-bottom-color: transparent;
      content: '';
    }
  }
}
</style>
